import React, { useState, useEffect, useRef } from 'react';
import Select from 'react-select';
import axios from 'axios';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import './AnnexureReport.css';
import jsPDF from "jspdf";
import "jspdf-autotable";
import ExcelJS from 'exceljs';
import { selectStyles } from '../../../assets/Styles/selectStyle';
import { getDistrict, getProjects, getStates, getClients, getProject, fetchMonths, getAnnexureReport } from '../../../services/monthlyAttendanceService';
import { capitalizeFirstLetter } from '../../../utils/validations';
const AnnexureReport = () => {
    const [state, setState] = useState(3);
    const [district, setDistrict] = useState(null);
    const [month, setMonth] = useState(null);
    const [year, setYear] = useState(null);
    const [stateOptions, setStateOptions] = useState([]);
    const [monthOptions, setMonthOptions] = useState([]);
    const [attendanceData, setAttendanceData] = useState([]);
    const [projectResponse, setProjectResponse] = useState([]);
    const [project, setProject] = useState(null);
    const [clientResponse, setClientResponse] = useState([]);
    const [clients, setClients] = useState(null);
    const effectRan = useRef(false);
    const [isFocused, setIsFocused] = useState({
        state: false,
        district: false,
        month: false,
        year: false,
        project: false,
        clients: false,
    });
    const [errors, setErrors] = useState({});
    const yearInputRef = useRef(null);
    const [hasFetched, setHasFetched] = useState(false); // New state to track fetch attempt
    const isFetchDataEnabled = state && month && year && project;
    const [designationSummary, setDesignationSummary] = useState([]);

    // Fetch states
    useEffect(() => {
        const fetchStates = async () => {
            try {
                // const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/states/nationality/1`);
                const data = await getStates();
                setStateOptions(
                    data.map(item => ({
                        value: item.id,
                        label: capitalizeFirstLetter(item.stateName),
                    }))
                );
            } catch (error) {
                console.error('Error fetching states:', error);
                setErrors(prev => ({ ...prev, general: 'Failed to load states' }));
            }
        };
        if (!effectRan.current) {
            fetchStates();
        }
        return () => {
            effectRan.current = true;
        }
    }, []);

    // Fetch months when year changes
    useEffect(() => {
        const loadMonths = async () => {
            try {
                const months = await fetchMonths(year);
                setMonthOptions(months);
            } catch (error) {
                console.error("Error fetching months:", error);
                setErrors((prev) => ({ ...prev, general: "Failed to load months" }));
            }
        };
        loadMonths();
    }, [year]);


    // Fetch districts when state changes
    useEffect(() => {
        if (state) {
            const fetchClients = async () => {
                try {
                    // const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/projects`);
                    const data = await getClients(state);
                    setClientResponse(data.map(client => ({
                        value: client.id,
                        label: client.clientName,
                    }))
                    );
                } catch (error) {
                    console.error('Error fetching states:', error);
                    setErrors(prev => ({ ...prev, general: 'Failed to load states' }));
                }
            };
            fetchClients();
        }
        else {
            setClients(null);
        }
    }, [state]);


    useEffect(() => {
        if (clients) {
            const fetchProjects = async () => {
                try {
                    const data = await getProject(clients);
                    setProjectResponse(data.map(pro => ({
                        value: pro.id,
                        label: pro.projectCode,
                    })));
                } catch (error) {
                    console.error('Error fetching projects:', error);
                    setErrors(prev => ({ ...prev, general: 'Failed to load projects' }));
                }
            };

            fetchProjects();
        } else {
            setProjectResponse([]); // Reset projects if no client is selected
        }
    }, [clients]);

    // Clear attendance data and fetch status when any dropdown changes
    useEffect(() => {
        setAttendanceData([]);
        // setOriginalData([]);
        setHasFetched(false);
    }, [state, district, month, year, project]); // ✅ Add project to depende

    const handleInputChange = (selectedOption, field) => {
        const value = selectedOption ? selectedOption.value : null;
        switch (field) {
            case 'state':
                setState(value);
                setDistrict(null);
                setMonth(null);
                setProject(null); // ✅ Clear project when state changes
                break;
            case 'clients':
                setClients(value);
                setProject(null);
                break;
            case 'month':
                setMonth(value);
                break;
            case 'project':
                setProject(value); // ✅ Handle project selection
                break;
            default:
                break;
        }
        setErrors(prev => ({ ...prev, [field]: null }));
    };

    const handleYearChange = (date) => {
        setYear(date ? date.getFullYear() : null);
        setErrors(prev => ({ ...prev, year: null }));
    };

    const validateForm = () => {
        const newErrors = {};
        if (!state) newErrors.state = 'State is required';
        //if (!district) newErrors.district = 'District is required';
        if (!month) newErrors.month = 'Month is required';
        if (!year) newErrors.year = 'Year is required';
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };
    const handleFetch = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        try {
            // 🔹 Fetch your existing API
            const data = await getAnnexureReport({
                stateId: state,
                districtId: district,
                clientId: clients,
                month,
                year,
                projectId: project,
            });

            console.log("Fetched Data:", data);

            // ✅ Map short codes → full names
            const designationMap = {
                PM: "Project Manager",
                PMDC: "Data Center Project Manager",
                "SYSTEM ADMIN": "System Administrator",
                APM: "Assistant Project Manager",
                "ITM TPDS": "IT Managers",
                CRO: "Control Room Operators",
                "HDO TPDS": "Help Desk Operators",
                CCO: "Call Center Operators",
                "TPDS DEO": "Godown Operators",
                HDOP: "Help Desk Operator ",
            };

            const designationOrder = [
                "PM",
                "PMDC",
                "SYSTEM ADMIN",
                "APM",
                "ITM TPDS",
                "CRO",
                "HDO TPDS",
                "CCO",
                "TPDS DEO",
                "HDOP"
            ];

            // ✅ Sort & rename your API data
            const sortedData = data
                .map((item) => ({
                    ...item,
                    fullName: designationMap[item.designationName] || item.designationName,
                }))
                .sort(
                    (a, b) =>
                        designationOrder.indexOf(a.designationName) -
                        designationOrder.indexOf(b.designationName)
                );

            setDesignationSummary(sortedData);
            console.log("Sorted Designation Summary:", sortedData);
        } catch (error) {
            console.error("Error fetching data:", error);
            setErrors((prev) => ({
                ...prev,
                general: "Failed to fetch report data",
            }));
            alert("Failed to fetch report data. Please try again.");
        }
    };

    const currentYear = new Date().getFullYear();
    const minDate = new Date(2000, 0, 1);
    const maxDate = new Date(currentYear, 11, 31);
    // Handle rate input and calculate total
    const handleRateChange = (e, index) => {
        const newRate = parseFloat(e.target.value) || 0;
        const daysInMonth = new Date(year, month, 0).getDate();

        setAttendanceData((prev) =>
            prev.map((row, i) => {
                if (i === index) {
                    const perDay = newRate / daysInMonth;
                    const total = perDay * row.totalPresentDays;
                    return { ...row, rate: newRate, totalAmount: total };
                }
                return row;
            })
        );
    };

    const generateExcel = async () => {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet("Annexure Report");

        // === Title ===
        worksheet.mergeCells("A1:F1");
        const titleCell = worksheet.getCell("A1");
        titleCell.value = "Annexure Report";
        titleCell.font = { size: 16, bold: true };
        titleCell.alignment = { vertical: "middle", horizontal: "center" };

        // === Report Date ===
        const now = new Date();
        const dd = String(now.getDate()).padStart(2, "0");
        const mm = String(now.getMonth() + 1).padStart(2, "0");
        const yyyy = now.getFullYear();
        const reportDateTime = `${dd}-${mm}-${yyyy}`;

        worksheet.getCell("E3").value = "Report Date";
        worksheet.getCell("E3").font = { bold: true };
        worksheet.getCell("E3").alignment = { horizontal: "right" };

        worksheet.getCell("F3").value = reportDateTime;
        worksheet.getCell("F3").alignment = { horizontal: "center" };

        worksheet.addRow([]);

        // === Labels and Values ===
        const stateLabel = stateOptions.find((opt) => opt.value === state)?.label || "N/A";
        const monthLabel = monthOptions.find((opt) => opt.value === month)?.label || "N/A";
        const projectLabel = projectResponse.find((opt) => opt.value === project)?.label || "N/A";
        const clientLabel = clientResponse.find((opt) => opt.value === clients)?.label || "N/A";
        const monthNumber = Number(month) || now.getMonth() + 1;
        const yearNumber = Number(year) || now.getFullYear();
        const daysInMonth = new Date(yearNumber, monthNumber, 0).getDate();

        // State
        worksheet.getCell("E5").value = "State";
        worksheet.getCell("E5").alignment = { horizontal: "right" };
        worksheet.getCell("F5").value = stateLabel;
        worksheet.getCell("F5").font = { bold: true };
        worksheet.getCell("F5").alignment = { horizontal: "center" };

        // Client
        worksheet.getCell("E6").value = "Client";
        worksheet.getCell("E6").alignment = { horizontal: "right" };
        worksheet.getCell("F6").value = clientLabel;
        worksheet.getCell("F6").font = { bold: true };
        worksheet.getCell("F6").alignment = { horizontal: "center" };

        // Project
        worksheet.getCell("E7").value = "Project";
        worksheet.getCell("E7").alignment = { horizontal: "right" };
        worksheet.getCell("F7").value = projectLabel;
        worksheet.getCell("F7").font = { bold: true };
        worksheet.getCell("F7").alignment = { horizontal: "center" };

        // Month-Year Line
        worksheet.mergeCells("A8:F8");
        worksheet.getCell("A8").value = {
            richText: [
                { text: "Month: ", font: { bold: false } },
                { text: monthLabel + "  ", font: { bold: true } },
                { text: "Year: ", font: { bold: false } },
                { text: (year || "N/A") + "  ", font: { bold: true } },
                { text: "|  No. of Days in The Month: ", font: { bold: false } },
                { text: String(daysInMonth), font: { bold: true } },
            ],
        };
        worksheet.getCell("A8").alignment = { horizontal: "left" };

        worksheet.addRow([]);
        worksheet.addRow([]);

        // === Table Header ===
        const header = [
            "S.No",
            "Business Operations Charges",
            "No. of Resources Deployed",
            "Total Days Present",
            "Rate per Month (INR)",
            "Total Amount (INR)",
        ];
        const headerRow = worksheet.addRow(header);
        headerRow.font = { bold: true, color: { argb: "FFFFFFFF" } };
        headerRow.eachCell((cell) => {
            cell.fill = {
                type: "pattern",
                pattern: "solid",
                fgColor: { argb: "4472C4" },
            };
            cell.border = {
                top: { style: "thin" },
                left: { style: "thin" },
                bottom: { style: "thin" },
                right: { style: "thin" },
            };
            cell.alignment = { horizontal: "center", vertical: "middle" };
        });

        // === Data Rows ===
        designationSummary.forEach((item, index) => {
            const row = worksheet.addRow([
                index + 1,
                item.fullName,
                item.noOfResourcesDeployed,
                item.totalDaysPresent,
                item.ratePerMonth,
                item.totalAmount,
            ]);
            // ✅ Apply Indian-style format to rate and total amount columns
            row.getCell(5).numFmt = '#,##,##0'; // Rate per Month (INR)
            row.getCell(6).numFmt = '#,##,##0'; // Total Amount (INR)
        });

        // === Totals ===
        const subTotal = designationSummary.reduce((sum, item) => sum + (item.totalAmount || 0), 0);
        const cgst = subTotal * 0.09;
        const sgst = subTotal * 0.09;
        const grandTotal = subTotal + cgst + sgst;

        worksheet.addRow([]);
        worksheet.addRow(["", "", "", "", "Sub Total", subTotal]);
        worksheet.addRow(["", "", "", "", "CGST @ 9%", cgst]);
        worksheet.addRow(["", "", "", "", "SGST @ 9%", sgst]);
        worksheet.addRow(["", "", "", "", "TOTAL", grandTotal]);
        worksheet.getCell(`F${worksheet.lastRow.number - 3}`).numFmt = '#,##,##0'; // Sub Total
        worksheet.getCell(`F${worksheet.lastRow.number - 2}`).numFmt = '#,##,##0.00'; // CGST
        worksheet.getCell(`F${worksheet.lastRow.number - 1}`).numFmt = '#,##,##0.00'; // SGST
        worksheet.getCell(`F${worksheet.lastRow.number}`).numFmt = '#,##,##0'; // TOTAL

        // Format Totals
        const lastRows = worksheet.lastRow.number;
        for (let i = lastRows - 3; i <= lastRows; i++) {
            const row = worksheet.getRow(i);
            row.font = { bold: true };
            row.eachCell((cell) => {
                cell.border = {
                    top: { style: "thin" },
                    left: { style: "thin" },
                    bottom: { style: "thin" },
                    right: { style: "thin" },
                };
            });
        }

        // === Adjust Column Widths ===
        worksheet.columns = [
            { width: 6 },
            { width: 40 },
            { width: 25 },
            { width: 20 },
            { width: 25 },
            { width: 25 },
        ];

        // === Download File ===
        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = `ANNEXURE-(A)_Report_${month}_${year}.xlsx`;
        link.click();
    };

    return (
        <div className="annexure-report-container">
            <div className="annexure-report-box">
                <h2 className="annexure-report-heading">ANNEXURE - (A) Report </h2>
                {errors.general && <div className="error-message">{errors.general}</div>}
                <form onSubmit={handleFetch}
                >
                    <div className="annexure-report-maincontainer">
                        <div className="annexure-report-container-part">
                            <div className={`annexure-report-floating-label-container ${isFocused.state || state ? 'float-label' : ''}`}>
                                <Select
                                    name="state"
                                    options={stateOptions}
                                    value={stateOptions.find(opt => opt.value === state) || null}
                                    onChange={option => handleInputChange(option, 'state')}
                                    onFocus={() => setIsFocused(prev => ({ ...prev, state: true }))}
                                    onBlur={() => setIsFocused(prev => ({ ...prev, state: false }))}
                                    placeholder=" "
                                    isClearable
                                    menuPortalTarget={document.body}
                                    styles={selectStyles}

                                />
                                <label><span className="required">*</span>State</label>
                                {errors.state && <div className="error-message">{errors.state}</div>}
                            </div>
                        </div>
                        <div className="annexure-report-container-part">

                        </div>
                    </div>
                    <div className="annexure-report-maincontainer">
                        <div className="annexure-report-container-part">
                            <div className={`annexure-report-floating-label-container ${isFocused.year || year ? 'float-label' : ''}`}>
                                <DatePicker
                                    selected={year ? new Date(year, 0, 1) : null}
                                    onChange={handleYearChange}
                                    onFocus={() => setIsFocused(prev => ({ ...prev, year: true }))}
                                    onBlur={() => setIsFocused(prev => ({ ...prev, year: false }))}
                                    dateFormat="yyyy"
                                    showYearPicker
                                    placeholderText=""
                                    className="annexure-report-date-picker"
                                    minDate={minDate}
                                    maxDate={maxDate}
                                    ref={yearInputRef}

                                    title="Select a year"
                                />
                                <label><span className="required">*</span>Year</label>
                                {errors.year && <div className="error-message">{errors.year}</div>}
                            </div>
                        </div>
                        <div className="annexure-report-container-part">
                            <div className={`annexure-report-floating-label-container ${isFocused.month || month ? 'float-label' : ''}`}>
                                <Select
                                    name="month"
                                    options={monthOptions}
                                    value={monthOptions.find(opt => opt.value === month) || null}
                                    onChange={option => handleInputChange(option, 'month')}
                                    onFocus={() => setIsFocused(prev => ({ ...prev, month: true }))}
                                    onBlur={() => setIsFocused(prev => ({ ...prev, month: false }))}
                                    placeholder=" "
                                    isClearable
                                    menuPortalTarget={document.body}
                                    styles={selectStyles}
                                />
                                <label><span className="required">*</span>Month</label>
                                {errors.month && <div className="error-message">{errors.month}</div>}
                            </div>
                        </div>
                    </div>
                    <div className="annexure-report-maincontainer">
                        <div className="annexure-report-container-part">
                            <div className={`annexure-report-floating-label-container ${isFocused.clients || clients ? 'float-label' : ''}`}>
                                <Select
                                    name="project"
                                    options={clientResponse}
                                    value={clientResponse.find(opt => opt.value === clients) || null}
                                    onChange={option => handleInputChange(option, 'clients')}
                                    onFocus={() => setIsFocused(prev => ({ ...prev, clients: true }))}
                                    onBlur={() => setIsFocused(prev => ({ ...prev, clients: false }))}
                                    placeholder=" "
                                    isClearable
                                    menuPortalTarget={document.body}
                                    styles={selectStyles}
                                />
                                <label><span className="required">*</span>Client</label>
                                {errors.project && <div className="error-message">{errors.project}</div>}
                            </div>

                        </div>
                        <div className="annexure-report-container-part">
                            <div className={`annexure-report-floating-label-container ${isFocused.project || project ? 'float-label' : ''}`}>
                                <Select
                                    name="project"
                                    options={projectResponse}
                                    value={projectResponse.find(opt => opt.value === project) || null}
                                    onChange={option => handleInputChange(option, 'project')}
                                    onFocus={() => setIsFocused(prev => ({ ...prev, project: true }))}
                                    onBlur={() => setIsFocused(prev => ({ ...prev, project: false }))}
                                    placeholder=" "
                                    isClearable
                                    menuPortalTarget={document.body}
                                    styles={selectStyles}
                                />
                                <label>
                                    <span className="required">*</span>Project
                                </label>
                                {errors.project && <div className="error-message">{errors.project}</div>}
                            </div>
                        </div>

                    </div>
                    <div className="annexure-report-btn-div">
                        <button type="submit" className="annexure-report-btn"
                            disabled={!isFetchDataEnabled}


                        >
                            Generate Report
                        </button>
                    </div>
                </form>
            </div>
            <div className="annexure-report-btn-div">
                <button
                    type="submit"
                    className="annexure-report-btn"
                    onClick={generateExcel}
                    disabled={designationSummary.length === 0}  //
                >
                    Download Excel
                </button>
            </div>

            <div className="annexure-Report-table">
                {/* ✅ Show table only when data is fetched */}
                {designationSummary.length > 0 && project === 1 && (
                    <div className="annexureR-mid-table">
                        <table className="annexureR-report">
                            <thead>
                                <tr>
                                    <th>S.No</th>
                                    <th>Business Operations Charges</th>
                                    <th>No. of Resources Deployed</th>
                                    <th>Total Days Present</th>
                                    <th>Rate per Month (INR)</th>
                                    <th>Total Amount (INR)</th>
                                </tr>
                            </thead>
                            <tbody>
                                {designationSummary.map((item, index) => (
                                    <tr key={index}>
                                        <td>{index + 1}</td>
                                        <td>{item.fullName}</td>
                                        <td>{item.noOfResourcesDeployed}</td>
                                        <td>{item.totalDaysPresent}</td>
                                        <td>
                                            {item.ratePerMonth.toLocaleString("en-IN", { maximumFractionDigits: 0 })}
                                        </td>
                                        <td>
                                            {item.totalAmount.toLocaleString("en-IN", { maximumFractionDigits: 0 })}
                                        </td>


                                    </tr>
                                ))}
                            </tbody>

                            {/* ✅ Footer calculations */}
                            <tfoot>
                                {(() => {
                                    const subTotal = designationSummary.reduce(
                                        (sum, item) => sum + (item.totalAmount || 0),
                                        0
                                    );
                                    const cgst = subTotal * 0.09;
                                    const sgst = subTotal * 0.09;
                                    const grandTotal = subTotal + cgst + sgst;

                                    return (
                                        <>
                                            <tr>
                                                <th colSpan="5" style={{ textAlign: "center" }}>
                                                    Sub Total
                                                </th>
                                                <th>{subTotal.toLocaleString("en-IN", { maximumFractionDigits: 0 })}</th>


                                            </tr>
                                            <tr>
                                                <th colSpan="5" style={{ textAlign: "center" }}>
                                                    CGST @ 9%
                                                </th>
                                                <th>{cgst.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</th>
                                            </tr>
                                            <tr>
                                                <th colSpan="5" style={{ textAlign: "center" }}>
                                                    SGST @ 9%
                                                </th>
                                                <th>{sgst.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</th>

                                            </tr>
                                            <tr>
                                                <th colSpan="5" style={{ textAlign: "center" }}>
                                                    TOTAL
                                                </th>
                                                <th>{grandTotal.toLocaleString("en-IN", { maximumFractionDigits: 0 })}</th>
                                            </tr>
                                        </>
                                    );
                                })()}
                            </tfoot>
                        </table>
                    </div>
                )}



                {/* ✅ ePDS Table — also show only after data fetch */}
                {designationSummary.length > 0 && project === 2 && (
                    <div className='annexureR-mid-table'>
                        <table className="annexureR-report">

                            <thead>
                                <tr>
                                    <th>S.No</th>
                                    <th>Business Operations Charges</th>
                                    <th>No. of Resources Deployed</th>
                                    <th>Total Days Present</th>
                                    <th>Rate per Month (INR)</th>
                                    <th>Total Amount (INR)</th>
                                </tr>
                            </thead>

                            <tbody>
                                {designationSummary
                                    .filter((item) => !(project === 2 && item.designationName === "HDO"))
                                    .map((item, index) => (
                                        <tr key={item.designationName}>
                                            <td>{index + 1}</td>
                                            <td>{item.fullName}</td>
                                            <td>{item.noOfResourcesDeployed}</td>
                                            <td>{item.totalDaysPresent}</td>
                                            <td>
                                                {item.ratePerMonth.toLocaleString("en-IN", { maximumFractionDigits: 0 })}
                                            </td>
                                            <td>
                                                {item.totalAmount.toLocaleString("en-IN", { maximumFractionDigits: 0 })}
                                            </td>
                                        </tr>
                                    ))}
                            </tbody>

                            {/* ✅ Footer calculations */}
                            <tfoot>
                                {(() => {
                                    const subTotal = designationSummary.reduce(
                                        (sum, item) => sum + (item.totalAmount || 0),
                                        0
                                    );
                                    const cgst = subTotal * 0.09;
                                    const sgst = subTotal * 0.09;
                                    const grandTotal = subTotal + cgst + sgst;

                                    return (
                                        <>
                                            <tr>
                                                <th colSpan="5" style={{ textAlign: "center" }}>
                                                    Sub Total
                                                </th>
                                                <th>{subTotal.toLocaleString("en-IN", { maximumFractionDigits: 0 })}</th>


                                            </tr>
                                            <tr>
                                                <th colSpan="5" style={{ textAlign: "center" }}>
                                                    CGST @ 9%
                                                </th>
                                                <th>{cgst.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</th>
                                            </tr>
                                            <tr>
                                                <th colSpan="5" style={{ textAlign: "center" }}>
                                                    SGST @ 9%
                                                </th>
                                                <th>{sgst.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</th>

                                            </tr>
                                            <tr>
                                                <th colSpan="5" style={{ textAlign: "center" }}>
                                                    TOTAL
                                                </th>
                                                <th>{grandTotal.toLocaleString("en-IN", { maximumFractionDigits: 0 })}</th>
                                            </tr>
                                        </>
                                    );
                                })()}
                            </tfoot>

                        </table>
                    </div>
                )}

            </div>




        </div>
    );
};

export default AnnexureReport;